import { createBrowserRouter, Navigate } from "react-router";
import { AppErrorScreen } from "./components/AppErrorScreen";
import { RequireAuth } from "./auth/RequireAuth";
import { AppShell } from "./routes/AppShell";
import { Dashboard } from "./routes/Dashboard";
import { Login } from "./routes/Login";
import { ResetPassword } from "./routes/ResetPassword";
import { ConfirmEmail } from "./routes/ConfirmEmail";
import { Impersonate } from "./routes/Impersonate";
import { Clients } from "./routes/Clients";
import { ClientProfile } from "./routes/clients/ClientProfile";
import { People } from "./routes/clients/People";
import { PersonProfile } from "./routes/clients/PersonProfile";
import { Leads } from "./routes/Leads";
import { LeadDetail } from "./routes/LeadDetail";
import { Sessions } from "./routes/Sessions";
import { SessionDetail } from "./routes/SessionDetail";
import { ConductSession } from "./routes/ConductSession";
import { SessionMove } from "./routes/SessionMove";
import { SessionCancel } from "./routes/SessionCancel";
import { SessionClose } from "./routes/SessionClose";
import { SessionDelete } from "./routes/SessionDelete";
import { Payments } from "./routes/Payments";
import { SettingsShell } from "./routes/settings/SettingsShell";
import { AgreementsListing } from "./routes/settings/AgreementsListing";
import { DefaultsPane } from "./routes/settings/DefaultsPane";
import { AgreementEdit } from "./routes/settings/AgreementEdit";
import { AgreementCreate } from "./routes/settings/AgreementCreate";
import { CatalogShell } from "./routes/settings/catalog/CatalogShell";
import { CatalogIntro } from "./routes/settings/catalog/CatalogIntro";
import { BookingTypeEdit as CatalogServiceEdit } from "./routes/settings/catalog/BookingTypeEdit";
import { BookingTypeCreate as CatalogServiceCreate } from "./routes/settings/catalog/BookingTypeCreate";
import { PricingPlanEdit as CatalogPlanEdit } from "./routes/settings/catalog/PricingPlanEdit";
import { PricingPlanCreate as CatalogPlanCreate } from "./routes/settings/catalog/PricingPlanCreate";
import { PhoneBridgePage } from "./routes/settings/PhoneBridgePage";
import { CustomTrackersPage } from "./routes/settings/CustomTrackersPage";
import { ContactInfoPage } from "./routes/settings/ContactInfoPage";
import { SocialLinksPage } from "./routes/settings/SocialLinksPage";
import { WebsiteShell } from "./routes/website/WebsiteShell";
import { BlogListing } from "./routes/website/BlogListing";
import { BlogPostCreate } from "./routes/website/BlogPostCreate";
import { BlogPostEdit } from "./routes/website/BlogPostEdit";
// Landing page components kept on disk; routes redirect to Pages
// import { LandingPagesListing } from "./routes/website/LandingPagesListing";
// import { LandingPageCreate } from "./routes/website/LandingPageCreate";
// import { LandingPageEdit } from "./routes/website/LandingPageEdit";
import { FaqsListing } from "./routes/website/FaqsListing";
import { FaqCreate } from "./routes/website/FaqCreate";
import { FaqEdit } from "./routes/website/FaqEdit";
import { BrandingPage } from "./routes/website/branding/BrandingPage";
// Legacy routes redirect to the unified chrome editor
// import { NavEditor } from "./routes/website/navigation/NavEditor";
// import { FooterEditor } from "./routes/website/footer/FooterEditor";
import { CustomUrlsListing } from "./routes/website/CustomUrlsListing";
import { CustomUrlCreate } from "./routes/website/CustomUrlCreate";
import { CustomUrlEdit } from "./routes/website/CustomUrlEdit";
import { PagesListing } from "./routes/website/PagesListing";
import { PageCreate } from "./routes/website/PageCreate";
import { PageCanvasEditor } from "./routes/website/page-canvas/PageCanvasEditor";
import { AnalyticsPage } from "./routes/website/AnalyticsPage";
import { ChromeEditor } from "./routes/website/chrome/ChromeEditor";
import { Account } from "./routes/Account";
import { Billing } from "./routes/Billing";

export const router = createBrowserRouter([
  { path: "/login", element: <Login />, errorElement: <AppErrorScreen /> },
  {
    path: "/reset-password",
    element: <ResetPassword />,
    errorElement: <AppErrorScreen />,
  },
  {
    path: "/confirm-email",
    element: <ConfirmEmail />,
    errorElement: <AppErrorScreen />,
  },
  { path: "/impersonate", element: <Impersonate />, errorElement: <AppErrorScreen /> },
  {
    element: <RequireAuth />,
    errorElement: <AppErrorScreen />,
    children: [
      {
        path: "/",
        element: <AppShell />,
        children: [
          { index: true, element: <Navigate to="/dashboard" replace /> },
          { handle: { title: "Dashboard" }, path: "dashboard", element: <Dashboard /> },
          { handle: { title: "Leads" }, path: "leads", element: <Leads /> },
          { handle: { title: "Lead" }, path: "leads/:id", element: <LeadDetail /> },
          { handle: { title: "Clients" }, path: "clients", element: <Clients /> },
          // Static segments before the :id matcher.
          { handle: { title: "People" }, path: "clients/people", element: <People /> },
          { handle: { title: "Person" }, path: "clients/people/:id", element: <PersonProfile /> },
          { handle: { title: "Client" }, path: "clients/:id", element: <ClientProfile /> },
          { handle: { title: "Sessions" }, path: "sessions", element: <Sessions /> },
          { handle: { title: "Session" }, path: "sessions/:id", element: <SessionDetail /> },
          { path: "sessions/:id/conduct", element: <ConductSession /> },
          { path: "sessions/:id/move", element: <SessionMove /> },
          { path: "sessions/:id/cancel", element: <SessionCancel /> },
          { path: "sessions/:id/close", element: <SessionClose /> },
          { path: "sessions/:id/delete", element: <SessionDelete /> },
          { handle: { title: "Payments" }, path: "payments", element: <Payments /> },
          {
            handle: { title: "Website" },
            path: "website",
            element: <WebsiteShell />,
            children: [
              { index: true, element: <Navigate to="/website/pages" replace /> },
              { handle: { title: "Pages" }, path: "pages", element: <PagesListing /> },
              { handle: { title: "Blog" }, path: "blog", element: <BlogListing /> },
              { path: "landing", element: <Navigate to="/website/pages" replace /> },
              { handle: { title: "Custom URLs" }, path: "custom-urls", element: <CustomUrlsListing /> },
              { handle: { title: "FAQs" }, path: "faqs", element: <FaqsListing /> },
              { handle: { title: "Branding" }, path: "branding", element: <BrandingPage /> },
              { path: "navigation", element: <Navigate to="/website/chrome" replace /> },
              { path: "footer", element: <Navigate to="/website/chrome" replace /> },
              { handle: { title: "Analytics" }, path: "analytics", element: <AnalyticsPage /> },
            ],
          },
          { handle: { title: "Header & Footer" }, path: "website/chrome", element: <ChromeEditor /> },
          { handle: { title: "New page" }, path: "website/pages/new", element: <PageCreate /> },
          { handle: { title: "Page" }, path: "website/pages/:id", element: <PageCanvasEditor /> },
          { handle: { title: "New post" }, path: "website/blog/new", element: <BlogPostCreate /> },
          { handle: { title: "Post" }, path: "website/blog/:id", element: <BlogPostEdit /> },
          { path: "website/landing/new", element: <Navigate to="/website/pages/new" replace /> },
          { path: "website/landing/:id", element: <Navigate to="/website/pages" replace /> },
          { handle: { title: "New URL" }, path: "website/custom-urls/new", element: <CustomUrlCreate /> },
          { handle: { title: "Custom URL" }, path: "website/custom-urls/:id", element: <CustomUrlEdit /> },
          { handle: { title: "New FAQ" }, path: "website/faqs/new", element: <FaqCreate /> },
          { handle: { title: "FAQ" }, path: "website/faqs/:id", element: <FaqEdit /> },
          { handle: { title: "Account" }, path: "account", element: <Account /> },
          { handle: { title: "Billing" }, path: "billing", element: <Billing /> },
          {
            handle: { title: "Settings" },
            path: "settings",
            element: <SettingsShell />,
            children: [
              { index: true, element: <Navigate to="/settings/catalog" replace /> },
              {
                handle: { title: "Catalog" },
                path: "catalog",
                element: <CatalogShell />,
                children: [
                  { index: true, element: <CatalogIntro /> },
                  { path: "services/new", element: <CatalogServiceCreate /> },
                  { path: "services/:id", element: <CatalogServiceEdit /> },
                  { path: "plans/new", element: <CatalogPlanCreate /> },
                  { path: "plans/:id", element: <CatalogPlanEdit /> },
                ],
              },
              { handle: { title: "Agreements" }, path: "agreements", element: <AgreementsListing /> },
              { handle: { title: "New agreement" }, path: "agreements/new", element: <AgreementCreate /> },
              { handle: { title: "Defaults" }, path: "agreements/defaults", element: <DefaultsPane /> },
              { handle: { title: "Agreement" }, path: "agreements/:id", element: <AgreementEdit /> },
              { handle: { title: "Phone bridge" }, path: "phone-bridge", element: <PhoneBridgePage /> },
              { handle: { title: "Custom trackers" }, path: "website/custom-trackers", element: <CustomTrackersPage /> },
              { handle: { title: "Contact info" }, path: "contact", element: <ContactInfoPage /> },
              { handle: { title: "Social links" }, path: "social", element: <SocialLinksPage /> },
            ],
          },
          { path: "*", element: <Navigate to="/dashboard" replace /> },
        ],
      },
    ],
  },
]);
